import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Box
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { updateStock } from '../../services/PreparationService';
import IOSSwitch from '../Switch/IOSSwitch';

const EditStockModal = ({ open, onClose, onSave, preparation, locationId, showVisibilityToggle = false }) => {
  
  const normalizeNumber = (value) => value?.toString().replace(',', '.');
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  
  const [formData, setFormData] = useState({
    stock: '',
    minimumStock: '',
    suggestedStock: '',
    visibleInLocation: true,
    note: ''
  });

  useEffect(() => {
    if (preparation) {
      setFormData({
        stock: preparation.stock || '',
        minimumStock: preparation.minimumStock || '',
        suggestedStock: preparation.suggestedStock || '',
        visibleInLocation: preparation.visibleInLocation ?? true,
        note: '',
      });
    }
  }, [preparation]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    let errors = {};
    let isValid = true;

    const stockValue = normalizeNumber(formData.stock);
    const minStockValue = normalizeNumber(formData.minimumStock);
    const suggestedStockValue = normalizeNumber(formData.suggestedStock);

    if (!stockValue || isNaN(parseFloat(stockValue)) || parseFloat(stockValue) < 0) {
      errors.stock = t('stock_required');
      isValid = false;
    }

    if (minStockValue !== '' && (!minStockValue || isNaN(parseFloat(minStockValue)) || parseFloat(minStockValue) < 0)) {
      errors.minimumStock = t('min_stock_invalid');
      isValid = false;
    }

    if (suggestedStockValue !== '' && (!suggestedStockValue || isNaN(parseFloat(suggestedStockValue)) || parseFloat(suggestedStockValue) < 0)) {
      errors.suggestedStock = t('suggested_stock_invalid');
      isValid = false;
    }

    // El motivo solo es obligatorio si la cantidad de stock realmente cambia.
    const stockChanged = stockValue !== '' && parseFloat(stockValue) !== parseFloat(preparation?.stock ?? 0);
    if (stockChanged && (!formData.note || !formData.note.trim())) {
      errors.note = t('stock_edit_note_required') || 'Ingresá un motivo para este cambio';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const request = {
        location_id: locationId,
        stock: parseFloat(normalizeNumber(formData.stock)),
        minimum_stock: formData.minimumStock !== '' 
          ? parseFloat(normalizeNumber(formData.minimumStock)) 
          : undefined,
        suggested_stock: formData.suggestedStock !== '' 
          ? parseFloat(normalizeNumber(formData.suggestedStock)) 
          : undefined,
        visible_in_location: !!formData.visibleInLocation,
        note: formData.note.trim(),
      };

      const response = await updateStock(preparation.id, request);
      onSave(response);
      onClose();
    } catch (error) {
      console.error('Error updating product:', error);
      alert(t('error_updating_product'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('edit_stock')}</DialogTitle>
      <DialogContent>
        <p>{preparation ? t('edit_stock_description', { name: preparation.name }) : ''}</p>
        <Grid container spacing={2} style={{ marginTop: '4px' }}>
          {/* Stock */}
          <Grid item xs={12} sm={12}>
            <TextField
              label={t('stock')}
              type="number"
              value={formData.stock}
              onChange={(e) => handleChange('stock', e.target.value)}
              fullWidth
              margin="dense"
              error={!!formErrors.stock}
              helperText={formErrors.stock}
            />
          </Grid>

          {/* Stock mínimo */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('minimum_stock')}
              type="number"
              value={formData.minimumStock}
              onChange={(e) => handleChange('minimumStock', e.target.value)}
              fullWidth
              margin="dense"
              error={!!formErrors.minimumStock}
              helperText={formErrors.minimumStock}
            />
          </Grid>

          {/* Stock sugerido */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('suggested_stock')}
              type="number"
              value={formData.suggestedStock}
              onChange={(e) => handleChange('suggestedStock', e.target.value)}
              fullWidth
              margin="dense"
              error={!!formErrors.suggestedStock}
              helperText={formErrors.suggestedStock}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('stock_edit_note') || 'Motivo del cambio'}
              placeholder={t('stock_edit_note_placeholder') || 'Ej: corrección tras conteo físico'}
              value={formData.note}
              onChange={(e) => handleChange('note', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              margin="dense"
              error={!!formErrors.note}
              helperText={formErrors.note}
            />
          </Grid>

          {showVisibilityToggle && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 1 }}>
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={!!formData.visibleInLocation}
                      onChange={(e) => handleChange('visibleInLocation', e.target.checked)}
                    />
                  }
                  label={t('visible_in_location') || 'Visible en sucursal'}
                  sx={{
                    m: 0,
                    pr: 2,
                    alignItems: 'center',
                    '& .MuiFormControlLabel-label': {
                      pl: 1,
                    },
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <LoadingButton
          loading={loading}
          onClick={handleSave}
          variant="contained"
          color="primary"
        >
          {t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditStockModal;
